import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { Card, Badge, Spinner, Button } from '../../components/ui';
import { 
  Users, Search, RefreshCw, Shield, UserCheck, UserX,
  CheckCircle, XCircle, Clock, AlertCircle, Star,
  Mail, Phone, Calendar
} from 'lucide-react';
import './Admin.css';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [updating, setUpdating] = useState(false);
  const { request } = useApi();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await request('get', '/api/admin/users').catch(() => []);
      setUsers(data || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateRole = async (userId, newRole) => {
    if (!window.confirm(`Change user role to ${newRole}?`)) return;
    
    setUpdating(true);
    try {
      await request('put', `/api/admin/users/${userId}/role`, { role: newRole });
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update role');
    } finally {
      setUpdating(false);
    }
  };

  const toggleEnabled = async (userId) => {
    setUpdating(true);
    try {
      await request('put', `/api/admin/users/${userId}/toggle`, {});
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to toggle user');
    } finally {
      setUpdating(false);
    }
  };

  const getRoleBadge = (role) => {
    if (role === 'SUPER_ADMIN') {
      return <Badge variant="success"><Star size={12} /> SUPER_ADMIN</Badge>;
    }
    return <Badge variant="default"><Shield size={12} /> ORG_ADMIN</Badge>;
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.lastName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter.toUpperCase();
    return matchesSearch && matchesRole;
  });

  if (loading) {
    return (
      <div className="admin-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  const superAdminCount = users.filter(u => u.role === 'SUPER_ADMIN').length;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h1 className="admin-title">Users</h1>
          <p className="admin-subtitle">Manage all users on the platform</p>
        </div>
        <button className="refresh-btn" onClick={fetchUsers}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Stats Summary */}
      <div className="admin-stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: '24px' }}>
        <div className="admin-stat-card" style={{ borderLeft: '4px solid #3B82F6' }}>
          <div className="admin-stat-info">
            <div className="admin-stat-value">{users.length}</div>
            <div className="admin-stat-label">Total Users</div>
          </div>
        </div>
        <div className="admin-stat-card" style={{ borderLeft: '4px solid #8B5CF6' }}>
          <div className="admin-stat-info">
            <div className="admin-stat-value">{superAdminCount}</div>
            <div className="admin-stat-label">Super Admins</div>
          </div>
        </div>
        <div className="admin-stat-card" style={{ borderLeft: '4px solid #10B981' }}>
          <div className="admin-stat-info">
            <div className="admin-stat-value">{users.length - superAdminCount}</div>
            <div className="admin-stat-label">Org Admins</div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="admin-filters">
        <div className="search-wrapper">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-group">
          <Shield size={18} className="filter-icon" />
          <select 
            className="filter-select" 
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="all">All Roles</option>
            <option value="super_admin">Super Admin</option>
            <option value="org_admin">Org Admin</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <Card className="admin-table-container" padding="none">
        <div className="table-header">
          <h3 className="table-title">All Users</h3>
          <div className="table-info">{filteredUsers.length} users</div>
        </div>
        <div className="table-responsive">
          <table className="admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Organization</th>
                <th>Role</th>
                <th>Status</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr className="empty-row">
                  <td colSpan={7}>
                    <div className="empty-state">
                      <Users size={48} className="empty-icon" />
                      <h3>No users found</h3>
                      <p>Try adjusting your search or filter criteria</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="user-name">{user.firstName} {user.lastName}</div>
                    </td>
                    <td className="user-email">{user.email}</td>
                    <td>
                      {user.organizationName || <span className="no-org">No org</span>}
                    </td>
                    <td>{getRoleBadge(user.role)}</td>
                    <td>
                      {user.enabled ? (
                        <Badge variant="success"><CheckCircle size={12} /> Active</Badge>
                      ) : (
                        <Badge variant="danger"><XCircle size={12} /> Disabled</Badge>
                      )}
                    </td>
                    <td className="user-joined">
                      <Calendar size={12} className="inline mr-1" />
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td>
                      <div className="actions-cell">
                        {user.role === 'ORG_ADMIN' && (
                          <button 
                            className="action-btn promote"
                            onClick={() => updateRole(user.id, 'SUPER_ADMIN')}
                            disabled={updating}
                          >
                            <Star size={14} /> Promote
                          </button>
                        )}
                        {user.role === 'SUPER_ADMIN' && (
                          <button 
                            className="action-btn demote"
                            onClick={() => updateRole(user.id, 'ORG_ADMIN')}
                            disabled={updating}
                          >
                            <Shield size={14} /> Demote
                          </button>
                        )}
                        <button 
                          className="action-btn toggle"
                          onClick={() => toggleEnabled(user.id)}
                          disabled={updating}
                        >
                          {user.enabled ? <XCircle size={14} /> : <CheckCircle size={14} />}
                          {user.enabled ? ' Disable' : ' Enable'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default AdminUsers;